import {
    Box,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Typography,
} from '@mui/material';
import {
    CanAccess,
    Logout,
    AppBar as ReactAdminAppBar,
    UserMenu,
    useUserMenu,
    useGetIdentity,
    useGetOne,
} from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import { SearchOmnibox } from '@app/layout/SearchOmnibox';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import { getLinkToMember } from '@app/member/getLinkToMember';
import {
    MemberId,
    MemberSummary,
    Organization,
    OrganizationId,
    Profile,
} from '@wwf/sdk';
import { MemberAvatar } from '@app/member/show/MemberAvatar';
import { useResource } from '@app/misc/useResource';

type OrganizationBranding = Organization & { name?: string | null };

export const AppBar = () => {
    const { logo, title } = useConfigurationContext();
    const { identity } = useGetIdentity();

    const profile = identity?.profile as MemberSummary | undefined;
    const organizationId = profile?.organizationId;

    const { data: organization } = useGetOne<OrganizationBranding>(
        'organization',
        { id: organizationId as OrganizationId },
        { enabled: organizationId != null }
    );

    const logoId = organization?.logo?.id;
    const { data: organizationLogoUrl } = useResource(logoId);

    const orgName =
        typeof organization?.name === 'string' ? organization.name.trim() : '';
    const displayTitle = orgName !== '' ? orgName : title;
    const displayLogoSrc = organizationLogoUrl ?? logo;

    return (
        <ReactAdminAppBar
            color="primary"
            alwaysOn={false}
            userMenu={
                <UserMenu
                    icon={
                        <MemberAvatar
                            variant="sm"
                            record={identity?.profile as Profile}
                        />
                    }
                >
                    <ConfigurationMenu />
                    <CanAccess resource="member" action="list">
                        <StaffMembersMenu />
                    </CanAccess>
                    <Logout />
                </UserMenu>
            }
        >
            <Box
                flex={1}
                display="flex"
                justifyContent="flex-start"
                gap={1.5}
                alignItems="center"
            >
                <Box
                    component="img"
                    sx={{ height: 24 }}
                    src={displayLogoSrc as string}
                />
                <Typography sx={{ flex: 1 }} component="span" variant="h5">
                    {displayTitle}
                </Typography>
                <Box sx={{ flex: 1, maxWidth: 400, ml: 2 }}>
                    <SearchOmnibox />
                </Box>
            </Box>
        </ReactAdminAppBar>
    );
};

const StaffMembersMenu = () => {
    const { onClose } = useUserMenu() ?? {};
    return (
        <MenuItem
            component={RouterLink}
            to='/member?filter={"type":"STAFF"}'
            onClick={onClose}
        >
            <ListItemIcon>
                <PeopleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Staff members</ListItemText>
        </MenuItem>
    );
};

const ConfigurationMenu = () => {
    const { onClose } = useUserMenu() ?? {};
    const { identity } = useGetIdentity();
    if (!identity) return null;

    return (
        <MenuItem
            component={RouterLink}
            to={getLinkToMember(identity.id as MemberId)}
            onClick={onClose}
        >
            <ListItemIcon>
                <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>My info</ListItemText>
        </MenuItem>
    );
};
